import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Lines, SectionTag } from "../components/site/Motion";

const PAGES = {
  "microcar-elettrica": {
    seoTitle: "Microcar elettrica L6e | SWAPA ZIP per la città",
    seoDesc: "SWAPA ZIP è la microcar elettrica L6e per la città: 2 posti tandem, batterie rimovibili 6,45 kWh, ricarica domestica in 3,5 ore, fino a 100 km di autonomia. €13.990 IVA inclusa, pre-ordine con €200.",
    eyebrow: "Microcar elettrica L6e",
    h1: ["La microcar elettrica", "che la città aspettava."],
    intro: "SWAPA ZIP è un quadriciclo leggero 100% elettrico costruito con standard automobilistici: abitacolo chiuso, due posti tandem e batterie rimovibili che si ricaricano da una normale presa di casa. Si guida dai 14 anni con la patente AM.",
    hero: "/assets/hero-poster.jpg",
    heroAlt: "SWAPA ZIP, microcar elettrica L6e per la mobilità urbana",
    specs: [
      { v: "45", u: "km/h", l: "Velocità massima" },
      { v: "100", u: "km", l: "Autonomia dichiarata", p: "fino a" },
      { v: "6,45", u: "kWh", l: "Batterie rimovibili" },
      { v: "3,5", u: "ore", l: "Ricarica domestica" },
    ],
    points: [
      "Due batterie rimovibili con trolley: le ricarichi in casa, in ufficio, ovunque ci sia una presa",
      "Larghezza 1009 mm e due posti tandem ONE+1™: parcheggi dove un'auto non entra",
      "Struttura monoscocca MONOQ™ in alluminio e acciaio con stadi di assorbimento differenziati",
      "Niente cambio, niente carburante, manutenzione ridotta: pochi euro per cento chilometri",
    ],
    guideLinks: [
      { to: "/guide/microcar-elettrica", label: "Guida completa alla microcar elettrica" },
      { to: "/guide/costo-ricarica-microcar", label: "Quanto costa ricaricarla" },
      { to: "/guide/microcar", label: "Microcar: la guida completa" },
    ],
    box: { title: "€13.990 IVA inclusa.", text: "Full optional, senza optional da aggiungere. La prenoti online con €200, rimborsabili secondo le condizioni pubblicate, e la ritiri presso la rete SWAPA." },
  },
  "microcar-per-ragazzi": {
    seoTitle: "Microcar per ragazzi | SWAPA ZIP si guida a 14 anni",
    seoDesc: "SWAPA ZIP è la microcar per ragazzi che si guida a 14 anni con la patente AM: abitacolo chiuso, cinture di sicurezza, 45 km/h e costi di gestione contenuti. Prenota un test drive in famiglia.",
    eyebrow: "Microcar per ragazzi e famiglie",
    h1: ["La prima macchina.", "Chiusa, elettrica,", "limitata a 45 km/h."],
    intro: "SWAPA ZIP è la microcar elettrica che un ragazzo guida a 14 anni con la patente AM: abitacolo chiuso con cinture di sicurezza, velocità limitata per legge e una struttura monoscocca di stampo automobilistico. La indipendenza dei ragazzi, la tranquillità dei genitori.",
    hero: "/assets/lifestyle-door.webp",
    heroAlt: "SWAPA ZIP, microcar elettrica per ragazzi dai 14 anni con patente AM",
    specs: [
      { v: "14", u: "anni", l: "Età minima (patente AM)" },
      { v: "45", u: "km/h", l: "Velocità limitata per legge" },
      { v: "2", u: "posti", l: "Passeggero dai 16 anni" },
      { v: "1009", u: "mm", l: "Larghezza" },
    ],
    points: [
      "Abitacolo chiuso e cinture di sicurezza: protetti da pioggia, freddo e buio invernale",
      "Struttura monoscocca MONOQ™ in alluminio e acciaio con stadi di assorbimento differenziati",
      "Velocità massima limitata a 45 km/h per legge: meno energia in gioco, più margini",
      "Ricarica domestica con batterie rimovibili e gestione da pochi euro a settimana",
    ],
    guideLinks: [
      { to: "/guide/microcar-ragazzi", label: "Guida per genitori e famiglie" },
      { to: "/guide/patente-am", label: "Patente AM: regole verificate" },
      { to: "/guide/sicurezza-microcar", label: "Sicurezza delle microcar" },
    ],
    box: { title: "Venite a provarla insieme.", text: "La prima macchina si sceglie in famiglia: sedetevi a bordo, guardate gli ingombri, fate tutte le domande. Il test drive è il modo giusto di decidere." },
  },
};

export default function CommercialLanding({ pageKey }) {
  const c = PAGES[pageKey];
  useEffect(() => {
    document.title = c.seoTitle;
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", c.seoDesc);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `https://swapa.it/${pageKey}`);
    return () => { if (meta && prev != null) meta.setAttribute("content", prev); };
  }, [c, pageKey]);

  return (
    <div data-testid={`landing-${pageKey}`} className="bg-ink pb-28 pt-28 sm:pt-36">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">{c.eyebrow}</p>
        <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tighter">
          <Lines animate="load" lines={c.h1} stagger={0.12} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl font-serif text-lg italic leading-relaxed text-zinc-400">{c.intro}</p>
        </Reveal>

        <Reveal delay={0.2}>
          <img src={c.hero} alt={c.heroAlt} className="mt-12 aspect-[21/9] w-full border border-white/10 object-cover" />
        </Reveal>

        <section className="mt-14 grid grid-cols-2 gap-px border border-white/10 bg-white/10 lg:grid-cols-4" data-testid="landing-specs">
          {c.specs.map((s) => (
            <div key={s.l} className="bg-ink p-6 sm:p-8">
              <p className="font-display text-3xl font-extrabold text-white sm:text-4xl">
                {s.p && <span className="mr-1 text-sm font-normal text-zinc-500">{s.p}</span>}
                {s.v}
                <span className="ml-1 text-base text-swapa-yellow">{s.u}</span>
              </p>
              <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{s.l}</p>
            </div>
          ))}
        </section>

        <div className="mt-16 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <ul className="space-y-5">
              {c.points.map((p) => (
                <li key={p.slice(0, 24)} className="flex gap-4 border-b border-white/10 pb-5 text-base leading-relaxed text-zinc-300">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-swapa-yellow" />
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-swapa-yellow/30 bg-gradient-to-br from-swapa-yellow/[0.06] to-transparent p-8 sm:p-10">
              <p className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-white">{c.box.title}</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{c.box.text}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/test-drive" data-testid="landing-cta-testdrive"
                  className="group inline-flex items-center gap-2 bg-swapa-yellow px-6 py-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-black transition-colors hover:bg-white">
                  Prenota un test drive
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
                <Link to="/preordina" data-testid="landing-cta-preorder"
                  className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white transition-colors hover:border-swapa-yellow hover:text-swapa-yellow">
                  Preordina con €200
                </Link>
                <Link to="/network" data-testid="landing-cta-network"
                  className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white transition-colors hover:border-swapa-yellow hover:text-swapa-yellow">
                  Trova il centro più vicino
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        <section className="mt-16 border-t border-white/10 pt-10" data-testid="landing-guides">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Approfondimenti SWAPA Guide</p>
          <ul className="mt-6 flex flex-wrap gap-3">
            {c.guideLinks.map((g) => (
              <li key={g.to}>
                <Link to={g.to} className="inline-flex items-center gap-2 border border-white/10 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-swapa-yellow hover:text-swapa-yellow">
                  {g.label} <ArrowUpRight className="h-3 w-3" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
